const key = (parent, ordinal) => `${parent}@${ordinal}`;
/**
 * Index the rows.
 *
 * Order within a family follows `updatedAt` only as a tiebreak — the ORIGINAL is always first,
 * because "version 1" means the thread that was already there. Sorting purely by time would
 * renumber the versions every time someone answered in one of them, and a switcher whose numbering
 * moves under the reader is worse than no numbering.
 */
export function indexBranches(rows) {
    const families = new Map();
    const familyOf = new Map();
    const byId = new Map(rows.map((r) => [r.id, r]));
    // An ordinal of 0 is "not a version": a fork carries provenance without a divergence point, and
    // so does a row written before the ordinal existed.
    const branches = rows.filter((r) => r.branchedFrom && (r.branchedAtOrdinal ?? 0) > 0);
    for (const b of branches) {
        const k = key(b.branchedFrom, b.branchedAtOrdinal);
        let fam = families.get(k);
        if (!fam) {
            fam = { parent: b.branchedFrom, ordinal: b.branchedAtOrdinal, parentSeq: b.branchedAtSeq ?? 0, members: [b.branchedFrom] };
            families.set(k, fam);
            familyOf.set(b.branchedFrom, k);
        }
        fam.members.push(b.id);
        familyOf.set(b.id, k);
    }
    // Branches sorted by when they were made, so version 2 stays version 2. The parent stays at [0].
    for (const fam of families.values()) {
        const rest = fam.members.slice(1).sort((a, b) => (byId.get(a)?.updatedAt ?? 0) - (byId.get(b)?.updatedAt ?? 0));
        fam.members = [fam.members[0], ...rest];
    }
    return { families, familyOf, byId };
}
/**
 * The version position of a conversation at one of its messages.
 *
 * Two questions in one, because a turn asks both: "are there versions of the thread that split
 * HERE" (this conversation is the parent) and "am I one of several versions" (this conversation is
 * a branch, and the point is where it split). Both are asked with the same ordinal, which is what
 * makes the switcher appear on both sides of a split. Returning null for everything else is what
 * keeps it off the vast majority of turns.
 */
export function positionAt(ix, conversationId, 
/** The seq of the turn being rendered. */
seq, 
/** The seq of the open conversation's FIRST message — what turns an ordinal into a seq inside a
 *  branch. Omit in the parent's case, where the stored seq is already the answer. */
firstSeq = 0) {
    const direct = ix.families.get(key(conversationId, ordinalOfParent(ix, conversationId, seq)));
    const famKey = ix.familyOf.get(conversationId);
    const fam = direct ?? (famKey ? ix.families.get(famKey) : undefined);
    if (!fam)
        return null;
    const index = fam.members.indexOf(conversationId);
    if (index < 0)
        return null;
    // WHICH SEQ MARKS THE SPLIT depends on which side of it this conversation is. In the parent it is
    // the stored seq; in a branch it is that branch's own first message plus the ordinal, because the
    // two conversations do not share a seq space at all.
    const anchor = conversationId === fam.parent ? fam.parentSeq : firstSeq + fam.ordinal;
    if (seq !== anchor)
        return null;
    return {
        family: fam,
        index: index + 1,
        count: fam.members.length,
        prev: index > 0 ? fam.members[index - 1] : null,
        next: index < fam.members.length - 1 ? fam.members[index + 1] : null,
    };
}
/** The parent-side lookup: a family is keyed by (parent, ordinal), and the parent knows only the
 *  seq — so it finds its family by matching the stored parent seq. */
function ordinalOfParent(ix, conversationId, seq) {
    for (const fam of ix.families.values()) {
        if (fam.parent === conversationId && fam.parentSeq === seq)
            return fam.ordinal;
    }
    return -1;
}
/**
 * The path from the root to a conversation — the branch's ancestry.
 *
 * Used for the "you are three branches deep" case and to stop a cycle from ever being walked: a
 * malformed row that pointed at itself would otherwise loop forever, and provenance is written by
 * the server but read here, so it is treated as data rather than trusted.
 */
export function ancestry(ix, conversationId) {
    const seen = new Set();
    const out = [];
    let cur = conversationId;
    while (cur && !seen.has(cur)) {
        seen.add(cur);
        out.unshift(cur);
        cur = ix.byId.get(cur)?.branchedFrom || undefined;
    }
    return out;
}
//# sourceMappingURL=branches.js.map